import { useMemo, useState } from 'react';

type SortDirection = 'ascending' | 'descending';

export interface SortDescriptorCustom<T> {
  column: keyof T;
  direction?: SortDirection;
}

interface useTableSortingProps<T> {
  items: T[];
  initialSort: Omit<SortDescriptorCustom<T>, 'direction'>;
}

/**
 * Hook específico para ordenar datos de una tabla
 */
export const useTableSorting = <T>({
  items,
  initialSort,
}: useTableSortingProps<T>) => {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptorCustom<T>>(
    { ...initialSort, direction: 'ascending' },
  );

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const first = a[sortDescriptor.column];
      const second = b[sortDescriptor.column];

      // comparación para string y number
      if (
        (typeof first === 'string' || typeof first === 'number') &&
        (typeof second === 'string' || typeof second === 'number')
      ) {
        const cmp = first < second ? -1 : first > second ? 1 : 0;
        return sortDescriptor.direction === 'descending' ? -cmp : cmp;
      }

      // si no son comparables no se ordenan
      return 0;
    });
  }, [items, sortDescriptor]);

  return {
    sortedItems,
    sortDescriptor,
    setSortDescriptor,
  };
};
